<script lang="ts">
  import { tick, onDestroy } from 'svelte';
  import type { TableOfContentsItem } from '$types/content.js';

  export let items: TableOfContentsItem[] = [];

  let activeId: string = '';
  let visibleIds: Set<string> = new Set();
  let activeObserver: IntersectionObserver | null = null;
  let visibleObserver: IntersectionObserver | null = null;

  const STORAGE_KEY = `${__STORAGE_KEY_PREFIX__}-toc-collapsed`;

  let collapsed: boolean = (() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  })();

  function toggleCollapsed() {
    collapsed = !collapsed;
    try { localStorage.setItem(STORAGE_KEY, String(collapsed)); } catch { /* ignore */ }
  }

  async function setupObserver(newItems: TableOfContentsItem[]) {
    if (activeObserver) { activeObserver.disconnect(); activeObserver = null; }
    if (visibleObserver) { visibleObserver.disconnect(); visibleObserver = null; }
    activeId = '';
    visibleIds = new Set();
    if (!newItems.length) return;

    // Wait for {@html} in ArticlePage to finish updating the DOM
    await tick();

    // Narrow band at the top of the viewport — determines the primary active heading
    activeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) activeId = entry.target.id;
        }
      },
      { rootMargin: '-80px 0px -80% 0px' },
    );

    // Full viewport (minus navbar) — tracks every heading currently on screen
    visibleObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visibleIds.add(entry.target.id);
          else visibleIds.delete(entry.target.id);
        }
        visibleIds = visibleIds; // trigger reactivity
      },
      { rootMargin: '-80px 0px 0px 0px' },
    );

    for (const item of newItems) {
      const el = document.getElementById(item.id);
      if (el) {
        activeObserver.observe(el);
        visibleObserver.observe(el);
      }
    }
  }

  $: setupObserver(items);

  onDestroy(() => {
    if (activeObserver) activeObserver.disconnect();
    if (visibleObserver) visibleObserver.disconnect();
  });

  function scrollToHeading(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
</script>

{#if items.length > 0}
  <aside class="toc" class:collapsed>
    <button class="toc-toggle" on:click={toggleCollapsed}>
      <h3>{__STRINGS__.article.onThisPage}</h3>
      <svg class="chevron" class:open={!collapsed} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <polyline points="1,1 5,5 9,1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    {#if !collapsed}
      <nav>
        <ul>
          {#each items as item}
            <li class="level-{item.level}" class:active={activeId === item.id} class:visible={visibleIds.has(item.id) && activeId !== item.id}>
              <a href="#{item.id}" on:click|preventDefault={() => scrollToHeading(item.id)}>
                {item.text}
              </a>
            </li>
          {/each}
        </ul>
      </nav>
    {/if}
  </aside>
{/if}

<style>
  .toc {
    padding: 0.75rem 1rem 1rem;
    position: sticky;
    top: 72px;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    background: var(--color-background, #fff);
    z-index: 1;
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    font-size: 0.9rem;
  }

  .toc-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    margin-bottom: 0.5rem;
  }

  .collapsed .toc-toggle {
    margin-bottom: 0;
  }

  h3 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-text-muted, #666);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .chevron {
    width: 0.6rem;
    height: 0.4rem;
    color: var(--color-text-muted, #666);
    flex-shrink: 0;
    transform: rotate(0deg);
    transition: transform 0.2s ease;
    overflow: visible;
  }

  .chevron.open {
    transform: rotate(180deg);
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin: 0;
  }

  a {
    display: block;
    padding: 0.25rem 0.5rem;
    color: var(--color-text-muted, #666);
    text-decoration: none;
    font-size: 0.85rem;
    border-left: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
  }

  a:hover {
    color: var(--color-text, #333);
  }

  li.active a {
    color: var(--color-primary, #e94560);
    border-left-color: var(--color-primary, #e94560);
  }

  li.visible a {
    color: var(--color-text, #333);
    border-left-color: color-mix(in srgb, var(--color-primary, #e94560) 35%, transparent);
  }

  .level-3 a {
    padding-left: 1rem;
  }

  .level-4 a {
    padding-left: 1.5rem;
  }

  .level-5 a,
  .level-6 a {
    padding-left: 2rem;
  }
</style>
